import React, { useEffect, useRef, useState } from "react";
import LocationSearch from "../../widgets/LocationSearch";
import ImageUpload from "../../widgets/ImageUpload";
import UsernameField from "../../widgets/UsernameField";
import CreatePasswordField from "../../widgets/CreatePasswordField";
import PhoneInput from "../ui/PhoneInput";
import { suggestUsername } from "../../api/api";
import { isValidEmail } from "../../lib/accountCredentials";
import { isValidE164 } from "../ui/phoneUtils";

/**
 * Individual signup profile fields. City / state is optional.
 *
 * @param {Object} props
 * @param {Object} props.formData
 * @param {function(Object): void} props.onFormDataUpdate
 * @param {function(): void} props.onBack
 * @param {function(): void} props.onNext
 * @returns {JSX.Element}
 */
export default function UserInfo({ formData, onFormDataUpdate, onBack, onNext }) {
  const [errors, setErrors] = useState({});
  const [usernameValid, setUsernameValid] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const usernameEditedRef = useRef(false);
  const updateFormRef = useRef(onFormDataUpdate);
  updateFormRef.current = onFormDataUpdate;

  useEffect(() => {
    const firstName = (formData.first_name || "").trim();
    const lastName = (formData.last_name || "").trim();
    if (!firstName || !lastName || usernameEditedRef.current) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const result = await suggestUsername(firstName, lastName);
      if (cancelled || usernameEditedRef.current || !result.username) return;
      updateFormRef.current({ username: result.username });
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [formData.first_name, formData.last_name]);

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   * @returns {void}
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    onFormDataUpdate({ [name]: value });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  /**
   * @returns {boolean}
   */
  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name || formData.first_name.trim() === "") {
      newErrors.first_name = "First name is required";
    }
    if (!formData.last_name || formData.last_name.trim() === "") {
      newErrors.last_name = "Last name is required";
    }
    if (!formData.email || formData.email.trim() === "") {
      newErrors.email = "Email is required";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (formData.phone && formData.phone.trim() && !isValidE164(formData.phone.trim())) {
      newErrors.phone = "Please enter a valid phone number";
    }
    if (!formData.username || formData.username.trim() === "") {
      newErrors.username = "Username is required";
    } else if (!usernameValid) {
      newErrors.username = "Choose an available username";
    }
    if (!passwordValid) {
      newErrors.password = "Password does not meet the requirements";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <div className="space-y-6">
      <h2 className="mb-4 text-2xl font-bold text-text-primary">Your information</h2>
      <div className="space-y-6">
        <div className="rounded-lg border border-border p-4">
          <ImageUpload
            value={formData.pfp_url || ""}
            onChange={(imageData) => {
              onFormDataUpdate({
                pfp_url: imageData.url || "",
                pfp_key: imageData.key || "",
              });
            }}
            label="Profile photo"
          />
          <p className="mt-2 text-xs text-text-muted">
            Optional. You can also add or change this later from your profile.
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-text-primary">Required information</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-text-secondary">
                First name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name || ""}
                onChange={handleChange}
                className={`w-full rounded-md border bg-secondary px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent ${
                  errors.first_name ? "border-error" : "border-border"
                }`}
                placeholder="First name"
              />
              {errors.first_name ? <p className="mt-1 text-sm text-error">{errors.first_name}</p> : null}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-text-secondary">
                Last name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name || ""}
                onChange={handleChange}
                className={`w-full rounded-md border bg-secondary px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent ${
                  errors.last_name ? "border-error" : "border-border"
                }`}
                placeholder="Last name"
              />
              {errors.last_name ? <p className="mt-1 text-sm text-error">{errors.last_name}</p> : null}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">
              Email <span className="text-error">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email || ""}
              onChange={handleChange}
              className={`w-full rounded-md border bg-secondary px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent ${
                errors.email ? "border-error" : "border-border"
              }`}
              placeholder="you@example.com"
            />
            {errors.email ? <p className="mt-1 text-sm text-error">{errors.email}</p> : null}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary" htmlFor="user-phone">
              Phone
            </label>
            <PhoneInput
              id="user-phone"
              value={formData.phone || ""}
              invalid={Boolean(errors.phone)}
              onChange={(phone) => {
                onFormDataUpdate({ phone });
                if (errors.phone) {
                  setErrors((prev) => ({ ...prev, phone: "" }));
                }
              }}
            />
            {errors.phone ? <p className="mt-1 text-sm text-error">{errors.phone}</p> : null}
          </div>

          <UsernameField
            id="user-username"
            label="Username"
            value={formData.username || ""}
            onChange={(username) => {
              setUsernameValid(false);
              onFormDataUpdate({ username });
              if (errors.username) {
                setErrors((prev) => ({ ...prev, username: "" }));
              }
            }}
            onUserEdit={() => {
              usernameEditedRef.current = true;
            }}
            onValidityChange={setUsernameValid}
            inputClassName="h-10 bg-secondary"
          />
          {errors.username && !usernameValid ? (
            <p className="-mt-2 text-sm text-error">{errors.username}</p>
          ) : null}

          <CreatePasswordField
            id="user-password"
            value={formData.password || ""}
            onChange={(password) => {
              onFormDataUpdate({ password });
              if (errors.password) {
                setErrors((prev) => ({ ...prev, password: "" }));
              }
            }}
            showConfirm
            confirmValue={formData.confirm_password || ""}
            onConfirmChange={(confirm_password) => onFormDataUpdate({ confirm_password })}
            onValidityChange={setPasswordValid}
            inputClassName="h-10 bg-secondary"
          />
          {errors.password && !passwordValid ? (
            <p className="-mt-2 text-sm text-error">{errors.password}</p>
          ) : null}

          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">
              City / state
            </label>
            <LocationSearch
              value={{
                value: formData.location || "",
                lat: formData.latitude,
                lon: formData.longitude,
              }}
              onChange={(locationData) => {
                onFormDataUpdate({
                  location: locationData.value,
                  latitude: locationData.lat,
                  longitude: locationData.lon,
                });
              }}
              placeholder="Search for a city..."
              searchType="city"
            />
            <p className="mt-2 text-xs text-text-muted">Optional. You can add this later from your profile.</p>
          </div>
        </div>
      </div>

      <div className="flex justify-between border-t border-border pt-6">
        <button
          type="button"
          onClick={onBack}
          className="rounded-md border border-border bg-secondary px-6 py-2 text-text-primary transition hover:bg-tertiary"
        >
          Back
        </button>
        <button
          type="button"
          onClick={() => {
            if (validateForm()) onNext();
          }}
          className="cursor-pointer rounded-md bg-accent px-6 py-2 text-text-on-accent transition hover:bg-accent-hover"
        >
          Next
        </button>
      </div>
    </div>
  );
}
